import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import produce from "immer";

import { PushNotificationStatus } from "../models/push-notification-status.enum";
import { PushNotification } from "../models/push-notification.model";
import { PushNotificationActions } from "./push-notification.actions";

export interface PushNotificationStateModel {
    notifications: PushNotification[];
    unreadCount: number;
}

const defaults: PushNotificationStateModel = {
    notifications: [],
    unreadCount: 0,
};

@Injectable()
@State<PushNotificationStateModel>({
    name: "pushNotifications",
    defaults: defaults,
})
export class PushNotificationState {
    @Action(PushNotificationActions.LoadStoredNotifications)
    public loadStoredNotifications(ctx: StateContext<PushNotificationStateModel>): void {
        // This action is dispatched when loading notifications from IndexedDB starts
        // The actual notifications are added via AddNotification actions
        console.log("Loading stored notifications from IndexedDB");
    }

    @Action(PushNotificationActions.AddNotification)
    public addNotification(
        ctx: StateContext<PushNotificationStateModel>,
        action: PushNotificationActions.AddNotification,
    ): void {
        const payload = action.payload.notification;

        if (!payload.data?.notificationId) {
            console.warn("Notification missing notificationId, skipping store");
            return;
        }

        const notification: PushNotification = {
            notificationId: payload.data.notificationId,
            title: payload.title,
            body: payload.body,
            icon: payload.icon,
            badge: payload.badge,
            tag: payload.tag,
            renotify: payload.renotify,
            url: payload.data?.url,
            // Preserve status from IndexedDB if loading stored notifications, otherwise default to Unread
            status: payload.data?.status || PushNotificationStatus.Unread,
            // Preserve receivedAt from IndexedDB if loading stored notifications, otherwise use current time
            receivedAt: payload.data?.receivedAt || new Date().toISOString(),
            actions: payload.actions,
        };

        ctx.setState(produce((draft) => {
            // Check if notification already exists
            const existingIndex = draft.notifications.findIndex((n) =>
                n.notificationId === notification.notificationId
            );

            if (existingIndex === -1) {
                // Add new notification at the beginning
                draft.notifications.unshift(notification);
                draft.unreadCount = draft.notifications.filter((n) =>
                    n.status === PushNotificationStatus.Unread
                ).length;
            }
        }));
    }

    @Action(PushNotificationActions.GetAllNotifications)
    public getAllNotifications(ctx: StateContext<PushNotificationStateModel>): PushNotification[] {
        return ctx.getState().notifications;
    }

    @Action(PushNotificationActions.GetUnreadNotifications)
    public getUnreadNotifications(ctx: StateContext<PushNotificationStateModel>): PushNotification[] {
        return ctx.getState().notifications.filter((n) => n.status === PushNotificationStatus.Unread);
    }

    @Action(PushNotificationActions.GetUnreadCount)
    public getUnreadCount(ctx: StateContext<PushNotificationStateModel>): number {
        const unreadCount =
            ctx.getState().notifications.filter((n) => n.status === PushNotificationStatus.Unread).length;
        ctx.patchState({ unreadCount });
        return unreadCount;
    }

    @Action(PushNotificationActions.SetNotificationStatus)
    public setNotificationStatus(
        ctx: StateContext<PushNotificationStateModel>,
        action: PushNotificationActions.SetNotificationStatus,
    ): void {
        ctx.setState(produce((draft) => {
            const notification = draft.notifications.find((n) => n.notificationId === action.notificationId);
            if (notification) {
                notification.status = action.status;
                draft.unreadCount = draft.notifications.filter((n) =>
                    n.status === PushNotificationStatus.Unread
                ).length;
            }
        }));
    }

    @Action(PushNotificationActions.MarkAllAsRead)
    public markAllAsRead(ctx: StateContext<PushNotificationStateModel>): void {
        ctx.setState(produce((draft) => {
            draft.notifications.forEach((n) => {
                n.status = PushNotificationStatus.Read;
            });
            draft.unreadCount = 0;
        }));
    }

    @Action(PushNotificationActions.ClearAllNotifications)
    public clearAllNotifications(ctx: StateContext<PushNotificationStateModel>): void {
        ctx.setState(defaults);
    }
}
